import React, { useState, useCallback, useMemo } from 'react';
import type { FC } from 'react';
import type { VariantOf } from 'variant';
import { match, variant } from 'variant';
import kebabCase from 'lodash/kebabCase.js';
import {
  Link,
  SelectGroupsInput,
  SelectInput,
  Spinner,
  TextInput,
  useAsync,
} from '@wix/cli-ui-kit';
import type { SelectGroup } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import type { BackOfficeExtensionContainer } from '@wix/extensions-data-client';
import type { ActionSchema } from '@wix/cli-app-definitions';
import { useDevCenterClient } from '@wix/dev-center-client';
import {
  useValidation,
  createDashboardMenuPluginValidation,
} from '../../../../dev-center/components/validations/index.js';
import { generateDashboardMenuPlugin } from './generate.js';
import { useGenerationTask } from '../../hooks/use-generation-task.js';
import { useProjectModel } from '../../../../model.js';
import { NavigateToPage } from './NavigateToPage.js';
import { OpenModal } from './OpenModal.js';
import { AppRequirement } from '../../AppRequirement.js';
import { useExtensions } from '../../../extensions/useExtensions.js';
import { useWixCliAppBi } from '../../../../bi/index.js';
import { InstallDependencies } from '../../InstallDependencies.js';
import { MultipleAppRequirement } from '../../MultipleAppRequirement.js';
import { DashboardMenuPluginGeneratedNote } from './DashboardMenuPluginGeneratedNote.js';
import type { SlotsApp } from '../DashboardPlugin.js';

const DashboardMenuPluginAction = variant({
  NavigateToPage: {},
  OpenModal: {},
});

type DashboardMenuPluginAction = VariantOf<typeof DashboardMenuPluginAction>;

const docsLink = (externalId?: string | null) =>
  new URL(
    `?portalSlug=sdk&externalId=${externalId}&fallbackUri=/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-plugins/dashboard-menu-plugins/dashboard-menu-plugin-extensions&platform=CLI`,
    'https://dev.wix.com/_api/docs-redirect/by-external-id'
  ).href;

export const DashboardMenuPluginWizard: FC<{
  pluginsMap: Record<string, BackOfficeExtensionContainer>;
  slotsApp: SlotsApp;
}> = ({ slotsApp, pluginsMap }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const { addRequiredApp } = useDevCenterClient();
  const { getExtensions } = useExtensions();

  const extensionType = t(
    'generate_command.flow.dashboard_menu_plugin'
  ).toLowerCase();

  const dashboardMenuPlugins = getExtensions('DashboardMenuPlugin');

  const [backofficeMenuItem, setBackofficeMenuItem] =
    useState<BackOfficeExtensionContainer>();
  const [actionType, setActionType] = useState<DashboardMenuPluginAction>();
  const [requireAppsIds, setRequireAppsIds] = useState<string[]>();
  const [action, setAction] = useState<ActionSchema>();
  const [folder, setFolder] = useState<string>();
  const [title, setTitle] = useState<string>();
  const [finishedInstallation, setFinishedInstallation] =
    useState<boolean>(false);

  const defaultFolderName = kebabCase(title);

  const [additionalInfo, setAdditionalInfo] =
    useState<Record<string, unknown>>();

  const validateMenuPluginFolder = useValidation(
    createDashboardMenuPluginValidation(dashboardMenuPlugins)
  );

  const generationTask = useGenerationTask(async () => {
    if (backofficeMenuItem && action && folder && title && requireAppsIds) {
      const requireAppId = requireAppsIds.at(0);

      if (requireAppId) {
        await addRequiredApp({
          appId: model.config.appId,
          appIdToRequire: requireAppId,
        });
      }

      return generateDashboardMenuPlugin(
        model.projectFolder,
        backofficeMenuItem.component.componentId,
        action,
        folder,
        title
      );
    }
  }, [backofficeMenuItem, action, folder, title]);

  const handleActionSelected = useCallback(
    (action: ActionSchema) => setAction(action),
    []
  );

  const { status } = useAsync(async () => {
    const backofficeExtensionWidgets = Object.values(
      slotsApp.backofficePages.reduce<
        Record<string, SelectGroup<BackOfficeExtensionContainer>>
      >((extensionsByPages, backofficePage) => {
        extensionsByPages[backofficePage.component.componentId] ??= {
          title:
            backofficePage.component.data.backOfficePage.metaData?.title ??
            backofficePage.component.name,
          items: [],
        };

        for (const slotId of backofficePage.component.data.backOfficePage
          .slotIds) {
          const backofficeMenuItem = pluginsMap[slotId];
          if (backofficeMenuItem) {
            extensionsByPages[backofficePage.component.componentId]?.items.push(
              {
                title:
                  backofficeMenuItem.component.data.backOfficeExtensionContainer
                    .slotData?.displayName ?? backofficeMenuItem.component.name,
                description: (
                  <Trans
                    i18nKey="generate_command.dashboard_menu_plugin.what_to_extend.item_description"
                    components={[
                      <Link
                        url={docsLink(backofficeMenuItem.component.externalId)}
                      />,
                    ]}
                  />
                ),
                key: backofficeMenuItem.component.componentId,
                value: backofficeMenuItem,
              }
            );
          }
        }

        return extensionsByPages;
      }, {})
    );

    for (const backofficeExtensionWidget of backofficeExtensionWidgets) {
      backofficeExtensionWidget.items.sort((a, b) =>
        a.title.localeCompare(b.title)
      );
    }

    backofficeExtensionWidgets.sort((a, b) => a.title.localeCompare(b.title));

    return backofficeExtensionWidgets;
  }, []);

  const task = useMemo(
    () =>
      match(generationTask, {
        Success: ({ hasUpdatedDependencies, newFiles }) => {
          if (!hasUpdatedDependencies) {
            setFinishedInstallation(true);
          }
          return (
            <>
              {hasUpdatedDependencies && (
                <InstallDependencies
                  onFinish={() => setFinishedInstallation(true)}
                />
              )}
              {finishedInstallation && backofficeMenuItem && (
                <DashboardMenuPluginGeneratedNote
                  backofficeMenuItem={backofficeMenuItem}
                  generatedFiles={newFiles}
                />
              )}
            </>
          );
        },
        Loading: () => (
          <Spinner
            text={t('generate_command.generation.loading', {
              subject: extensionType,
            })}
          />
        ),
        Error: () => null,
        NotRequested: () => null,
      }),
    [
      generationTask,
      setFinishedInstallation,
      t,
      extensionType,
      finishedInstallation,
      backofficeMenuItem,
    ]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: menuItemsGroups }) => (
      <>
        <SelectGroupsInput<BackOfficeExtensionContainer>
          label={t('generate_command.dashboard_menu_plugin.what_to_extend')}
          hideScroll
          info={
            <Trans
              i18nKey="generate_command.dashboard_menu_plugin.what_to_extend.description"
              // https://dev.wix.com/docs/build-apps/developer-tools/extensions/dashboard-extensions/dashboard-plugins/about-dashboard-menu-plugin-extensions
              components={[<Link url="https://wix.to/h8AJ7f5" />]}
            />
          }
          groups={menuItemsGroups}
          onSubmit={(item) => {
            const additionalInfoOfSelectedItem = {
              category: slotsApp.appName,
              extensionName: item.value.component.name,
              extensionType,
            };

            bi.cliFlowStepAnswered({
              question: t(
                'generate_command.dashboard_menu_plugin.what_to_extend'
              ),
              questionKey:
                'generate_command.dashboard_menu_plugin.what_to_extend',
              answer: item.title,
              additionalInfo: additionalInfoOfSelectedItem,
            });

            setAdditionalInfo(additionalInfoOfSelectedItem);
            setBackofficeMenuItem(item.value);
          }}
        />
        {backofficeMenuItem &&
          (slotsApp.requiredApps.length === 1 ? (
            <AppRequirement
              appId={model.config.appId}
              requiredApps={[
                {
                  id: slotsApp.requiredApps[0].appId,
                  name: slotsApp.requiredApps[0].appName,
                },
              ]}
              onSubmit={setRequireAppsIds}
            />
          ) : (
            <MultipleAppRequirement
              appId={model.config.appId}
              requiredApps={slotsApp.requiredApps.map((requiredApp) => ({
                id: requiredApp.appId,
                name: requiredApp.appName,
              }))}
              onSubmit={setRequireAppsIds}
            />
          ))}
        {requireAppsIds && (
          <SelectInput<DashboardMenuPluginAction>
            label={t('generate_command.dashboard_menu_plugin.what_to_do')}
            options={[
              {
                key: 'navigate-to-page',
                title: t(
                  'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page'
                ),
                value: DashboardMenuPluginAction.NavigateToPage(),
              },
              {
                key: 'open-modal',
                title: t(
                  'generate_command.dashboard_menu_plugin.what_to_do.open_modal'
                ),
                value: DashboardMenuPluginAction.OpenModal(),
              },
            ]}
            onSubmit={(action) => {
              bi.cliFlowStepAnswered({
                question: t(
                  'generate_command.dashboard_menu_plugin.what_to_do'
                ),
                questionKey:
                  'generate_command.dashboard_menu_plugin.what_to_do',
                answer: action.title,
                additionalInfo,
              });
              setActionType(action.value);
            }}
          />
        )}
        {actionType &&
          match(actionType, {
            NavigateToPage: () => (
              <NavigateToPage onActionSelected={handleActionSelected} />
            ),
            OpenModal: () => (
              <OpenModal onActionSelected={handleActionSelected} />
            ),
          })}
        {action && (
          <TextInput
            label={t('generate_command.dashboard_menu_plugin.choose_title')}
            placeholder={t(
              'generate_command.dashboard_menu_plugin.title_placeholder'
            )}
            onSubmit={(value) => {
              bi.cliFlowStepAnswered({
                question: t(
                  'generate_command.dashboard_menu_plugin.choose_title'
                ),
                questionKey:
                  'generate_command.dashboard_menu_plugin.choose_title',
                answer: value,
                additionalInfo,
              });
              setTitle(value);
            }}
          />
        )}
        {title && (
          <TextInput
            label={t('generate_command.dashboard_menu_plugin.choose_folder')}
            placeholder={defaultFolderName}
            onSubmit={(folder) => {
              bi.cliFlowStepAnswered({
                question: t(
                  'generate_command.dashboard_menu_plugin.choose_folder'
                ),
                questionKey:
                  'generate_command.dashboard_menu_plugin.choose_folder',
                answer: folder,
                additionalInfo,
              });
              setFolder(folder);
            }}
            validate={validateMenuPluginFolder}
          />
        )}
        {task}
      </>
    ),
  });
};
